// In hooks/clientHooks.js
import { bulkUploadClients } from '../models/clientAPI';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { getAllClients,getClientById, updateClient,createClient,deleteClient, ClientStatement } from '../models/clientAPI';

export const useFetchAllClients = (params = {}) => {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Stabilize params object to prevent infinite loops
  const stableParams = useMemo(() => params, [JSON.stringify(params)]);
  
  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const { clients: fetchedClients, total: fetchedTotal } = await getAllClients(stableParams);
      setClients(fetchedClients);
      setTotal(fetchedTotal);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [stableParams]);
  
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);
  
  return {
    clients,
    total,
    loading,
    error,
    refetch: fetchClients
  };
};


export const useFetchClientById = (id) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Define the fetching logic as a callable function using useCallback
  const fetchClient = useCallback(() => {
    if (id) {
      setLoading(true);
      setError(''); // Reset the error state on new fetch
      getClientById(id)
        .then(response => {
          setClient(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [id]);

  // Call fetchClient on initial mount and when `id` changes
  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  // Return everything including the fetchClient function, renamed as refetch for external usage
  return { client, loading, error, refetch: fetchClient };
};



export const useUpdateClient = () => {
  const [error, setError] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const handleUpdate = async (id, clientData) => {
    try {
      await updateClient(id, clientData);
      setIsUpdated(true);
    } catch (err) {
      setError(err.message);
      setIsUpdated(false);
    }
  };

  return { handleUpdate, error, isUpdated };
};


export const useCreateClient = () => {
  const [error, setError] = useState('');
  const [isCreated, setIsCreated] = useState(false);

  const handleCreate = async (clientData) => {
    try {
      await createClient(clientData);
      setIsCreated(true);
    } catch (err) {
      setError(err.message);
      setIsCreated(false);
    }
  };

  return { handleCreate, error, isCreated };
};


export const useDeleteClient = () => {
  const [error, setError] = useState('');
  const [isDeleted, setIsDeleted] = useState(false);

  const handleDelete = async (id) => {
    try {
      await deleteClient(id);
      setIsDeleted(true);
    } catch (err) {
      setError(err.message);
      setIsDeleted(false);
    }
  };

  return { handleDelete, error, isDeleted };
};



export const useBulkUploadClients = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleBulkUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await bulkUploadClients(formData);
      setUploadResult(response.data);
    } catch (err) {
      setError(err.message || 'An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  return { handleBulkUpload, loading, error, uploadResult };
};


export const useClientStatement = (clientId) => {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [shouldFetch, setShouldFetch] = useState(false);

  const fetchStatement = useCallback(() => {
    if (!clientId || !shouldFetch) return;

    setLoading(true);
    setError('');
    
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) {
      setError('Please select a date range');
      setLoading(false);
      return;
    }

    ClientStatement(
      clientId,
      startDate?.format('YYYY-MM-DD'),
      endDate?.format('YYYY-MM-DD')
    )
      .then(response => {
        console.log('Statement data:', response.data);
        setStatement(response.data);
        setLoading(false);
        setShouldFetch(false);
      })
      .catch(err => {
        console.error('Error fetching statement:', err);
        setError(err.message);
        setLoading(false);
        setShouldFetch(false);
      });
  }, [clientId, dateRange, shouldFetch]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  const handleSearch = () => {
    if (dateRange?.[0] && dateRange?.[1]) {
      setShouldFetch(true);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]); // Ensure we always set an array with nulls if dates is null
  };

  return { 
    statement, 
    loading, 
    error, 
    dateRange, 
    setDateRange: handleDateChange, // Use the new handler instead
    handleSearch,
  };
};